// theme.ts
export const themes = {
  // --- keep light exactly as originally provided ---
  light: {
   // bg-gradient-to-r from-[#1cbfbf] to-[#448452] p-4
    colors: {
      primary: '#1cbfbf', // teal-500
      secondary: '#3182CE',
      tertiary: '#2C5282',
      accent: '#805AD5',
      neutral: '#A0AEC0',
      success: '#448452',
      error: '#F56565',
      warning: '#B7791F',
      background: '#F0FDFA',
      surface: '#FFFFFF',
      card: '#FFFFFF',
      border: '#E2E8F0',
      borderMuted: '#CBD5E0',
      text: '#1A202C',
      textSecondary: '#4A5568',
      textMuted: '#718096',
      muted: '#718096',
      warningText: '#92400e',
      hover: '#0ea89a',
      active: '#0c8f86',
      focus: '#63B3ED',
      shadow: 'rgba(0,0,0,0.1)',
      inverseText: '#FFFFFF',
    },
  },

  // --- minimal semantic token set for dark (accent = primary, background separate) ---
  dark: {
    colors: {
      // accent/main interactive color (kept as teal-like accent, not black)
      primary: '#1cbfbf',
      // helpful secondary / tertiary accents
      secondary: '#60A5FA',
      tertiary: '#3B82F6',
      accent: '#F59E0B',
      neutral: '#6B7280',

      // surfaces / layout
      background: '#070708ff', // app background
      surface: '#10161D',    // surfaces like topbar
      card: '#0F1724',       // cards / elevated blocks

      // borders & dividers
      border: '#1F2937',
      borderMuted: '#2A3440',

      // text
      text: '#E6EEF3',
      textSecondary: '#B8C3CF',
      textMuted: '#8D98A4',
      muted: '#8D98A4',
      inverseText: '#0B1220', // used when displaying text on primary

      // states
      hover: '#22c1ac',
      active: '#16a79a',
      focus: '#93C5FD',
      shadow: 'rgba(2,6,23,0.6)',

      // feedback
      success: '#34D399',
      error: '#F87171',
      warning: '#FBBF24',
      warningText: '#92400e',
    },
  },

  // mild: low-contrast near-dark (compact tokens)
  mild: {
    colors: {
      primary: '#14161A', // rgb(20,22,26)
      secondary: '#191B1F', // rgb(25,27,31)
      tertiary: '#1E201F', // rgb(30,32,31)
      accent: '#ddfe65', // neon lime accent
      neutral: '#374151',
      success: '#34D399',
      warning: '#F59E0B',
      error: '#F87171',

      background: '#14161A',
      surface: '#191B1F',
      card: '#191B1F',
      border: '#2E2E3A',
      borderMuted: '#4A5568',

      text: '#EDEDED', // oklch(0.93 0 0)
      textSecondary: '#BFBFBF', // oklch(0.75 0 0)
      textMuted: '#BFBFBF',
      muted: '#BFBFBF',
      inverseText: '#FFFFFF',
      hover: '#1a1c20',
      active: '#0f1012',
      focus: '#60A5FA',
      shadow: 'rgba(0,0,0,0.5)',
      warningText: '#F59E0B',
    },
  },

  // darker: near-black theme with bright accent
  darker: {
    colors: {
      primary: '#343a3aff',
      secondary: '#404449ff',
      tertiary: '#1e293b',
      accent: '#DDFF6B',
      neutral: '#6b7280',
      background: '#030405',
      surface: '#07090A',
      card: '#0B0D0E',
      border: '#111419',
      borderMuted: '#374151',
      text: '#F6F7F8',
      textSecondary: '#D1D5DB',
      textMuted: '#9CA3AF',
      muted: '#9CA3AF',
      inverseText: '#000000',
      hover: '#05a2b0',
      active: '#048692',
      focus: '#3b82f6',
      shadow: 'rgba(0,0,0,0.8)',
      success: '#34D399',
      error: '#F87171',
      warning: '#F59E0B',
      warningText: '#F59E0B',
    },
  },

  // brand: compact, accessible brand tokens
  brand: {
    colors: {
      primary: '#2563EB',
      secondary: '#1D4ED8',
      tertiary: '#1e40af',
      accent: '#F97316',
      neutral: '#9ca3af',
      background: '#F9FAFB',
      surface: '#FFFFFF',
      card: '#FFFFFF',
      border: '#E5E7EB',
      borderMuted: '#d1d5db',
      text: '#0F172A',
      textSecondary: '#374151',
      textMuted: '#6B7280',
      muted: '#6B7280',
      inverseText: '#ffffff',
      hover: '#1f4fd6',
      active: '#193fb8',
      focus: '#60a5fa',
      shadow: 'rgba(0,0,0,0.1)',
      success: '#16A34A',
      error: '#DC2626',
      warning: '#D97706',
      warningText: '#92400e',
    },
  },

  // pastel: compact soft palette with semantic tokens (updated to girly theme)
  pastel: {
    colors: {
      primary: '#f081b8ff', // soft pink
      secondary: '#E1BEE7', // light lavender
      accent: '#FFF59D', // soft yellow
      background: '#FCF8F9', // very light pinkish
      surface: '#FFFFFF',
      card: '#FFFFFF',
      border: '#FCE4EC', // light pink border
      text: '#1A202C', // darker for better contrast
      textSecondary: '#2D3748',
      textMuted: '#4A5568',
      muted: '#4A5568',
      inverseText: '#FFFFFF',
      hover: '#F48FB1', // medium pink
      active: '#EC407A', // deeper pink
      success: '#81C784', // soft green
      error: '#EF5350', // soft red
      warning: '#FFB74D', // soft orange
      warningText: '#92400e',
    },
  },

  // girly: theme with more dark pink accents
  darkPestal: {
    colors: {
      primary: '#C2185B', // dark pink
      secondary: '#E91E63', // medium dark pink
      accent: '#FFC107', // gold accent
      background: '#FFF3E0', // light cream
      surface: '#FFFFFF',
      card: '#FFFFFF',
      border: '#F8BBD9', // soft pink border
      text: '#1A202C',
      textSecondary: '#2D3748',
      textMuted: '#374151',
      muted: '#374151',
      inverseText: '#FFFFFF',
      hover: '#AD1457', // darker pink
      active: '#880E4F', // even darker
      success: '#4CAF50',
      error: '#F44336',
      warning: '#FF9800',
      warningText: '#92400e',
    },
  },
} as const;

export type ThemeName = keyof typeof themes;
export type ThemeColors = typeof themes[ThemeName]['colors'];

/**
 * Small helper: return only the color record for a theme.
 * Usage: getTheme('dark').primary
 */
export function getTheme(themeName: ThemeName): ThemeColors {
  return themes[themeName].colors;
}

/**
 * Optional: map tokens to CSS variables (minimal).
 * Example usage:
 *   const vars = toCssVars(getTheme('dark'));
 *   // apply to document.documentElement.style.setProperty('--primary', vars['--primary'])
 */
export function toCssVars(colors: ThemeColors) {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(colors)) {
    out[`--${k}`] = String(v);
  }
  return out;
}
